const neutralParts = {
  start: {
    position: { x: 0, y: 0 },
    scale: { x: 100, y: 100 },
    rotation: { z: 0 },
  },
  left: {
    position: { x: 0, y: 0 },
    scale: { x: 100, y: 100 },
    rotation: { z: 0 },
  },
  right: {
    position: { x: 0, y: 0 },
    scale: { x: 100, y: 100 },
    rotation: { z: 0 },
  },
  end: {
    position: { x: 0, y: 0 },
    scale: { x: 100, y: 100 },
    rotation: { z: 0 },
  },
}

const withParts = (overrides = {}) =>
  Object.entries(neutralParts).reduce((acc, [part, base]) => {
    const next = overrides[part] || {}
    acc[part] = {
      position: { ...base.position, ...(next.position || {}) },
      scale: { ...base.scale, ...(next.scale || {}) },
      rotation: { ...base.rotation, ...(next.rotation || {}) },
    }
    return acc
  }, {})

const micro = (window, rateUnit = 'quarter') => ({
  region: 'sustain',
  shape: 'windowRelative',
  window,
  rateUnit,
  dotted: false,
  triplet: false,
  phaseBeats: 0,
  every: { unit: 'beats', value: rateUnit === 'eighth' ? 0.5 : 1 },
  span: { unit: 'beats', value: rateUnit === 'eighth' ? 0.5 : 1 },
  beatLocked: true,
  beatLockScope: 'global',
  amp: 1,
  yoyo: true,
})

export const GLOBAL_TEXT_MOTION_PRESETS = [
  {
    id: 'text.slide_in_left',
    name: 'Slide In Left',
    source: 'global',
    description: 'Enter from the left and settle centered.',
    intentTags: ['slide in left', 'enter left', 'from left', 'kinetic in'],
    version: 1,
    supportedProps: ['position.x'],
    parts: withParts({
      start: { position: { x: -200 } },
    }),
    offsets: { entry: 0.35, exit: 0 },
  },
  {
    id: 'text.slide_in_right',
    name: 'Slide In Right',
    source: 'global',
    description: 'Enter from the right and settle centered.',
    intentTags: ['slide in right', 'enter right', 'from right', 'kinetic in'],
    version: 1,
    supportedProps: ['position.x'],
    parts: withParts({
      start: { position: { x: 200 } },
    }),
    offsets: { entry: 0.35, exit: 0 },
  },
  {
    id: 'text.slide_in_top',
    name: 'Slide In Top',
    source: 'global',
    description: 'Enter from above and settle centered.',
    intentTags: ['slide in top', 'enter top', 'from top', 'drop in'],
    version: 1,
    supportedProps: ['position.y'],
    parts: withParts({
      start: { position: { y: 200 } },
    }),
    offsets: { entry: 0.35, exit: 0 },
  },
  {
    id: 'text.slide_in_bottom',
    name: 'Slide In Bottom',
    source: 'global',
    description: 'Enter from below and settle centered.',
    intentTags: ['slide in bottom', 'enter bottom', 'from bottom', 'rise in'],
    version: 1,
    supportedProps: ['position.y'],
    parts: withParts({
      start: { position: { y: -200 } },
    }),
    offsets: { entry: 0.35, exit: 0 },
  },
  {
    id: 'text.slide_out_left',
    name: 'Slide Out Left',
    source: 'global',
    description: 'Hold centered, then exit left.',
    intentTags: ['slide out left', 'exit left', 'leave left', 'kinetic out'],
    version: 1,
    supportedProps: ['position.x'],
    parts: withParts({
      end: { position: { x: -200 } },
    }),
    offsets: { entry: 0, exit: 0.35 },
  },
  {
    id: 'text.slide_out_right',
    name: 'Slide Out Right',
    source: 'global',
    description: 'Hold centered, then exit right.',
    intentTags: ['slide out right', 'exit right', 'leave right', 'kinetic out'],
    version: 1,
    supportedProps: ['position.x'],
    parts: withParts({
      end: { position: { x: 200 } },
    }),
    offsets: { entry: 0, exit: 0.35 },
  },
  {
    id: 'text.slide_top_to_bottom',
    name: 'Top To Bottom',
    source: 'global',
    description: 'Enter from above, hold centered, then exit below.',
    intentTags: ['top to bottom', 'enter top exit bottom', 'downward slide'],
    version: 1,
    supportedProps: ['position.y'],
    parts: withParts({
      start: { position: { y: 200 } },
      end: { position: { y: -200 } },
    }),
    offsets: { entry: 0.25, exit: 0.25 },
  },
  {
    id: 'text.slide_left_to_right',
    name: 'Left To Right',
    source: 'global',
    description: 'Enter from the left, hold centered, then exit right.',
    intentTags: ['left to right', 'enter left exit right', 'horizontal slide'],
    version: 1,
    supportedProps: ['position.x'],
    parts: withParts({
      start: { position: { x: -200 } },
      end: { position: { x: 200 } },
    }),
    offsets: { entry: 0.25, exit: 0.25 },
  },
  {
    id: 'text.pop_in',
    name: 'Pop In',
    source: 'global',
    description: 'Scale up quickly into the readable hold.',
    intentTags: ['pop in', 'scale in', 'quick pop', 'bounce in'],
    version: 1,
    supportedProps: ['scale.x', 'scale.y'],
    parts: withParts({
      start: { scale: { x: 72, y: 72 } },
    }),
    offsets: { entry: 0.22, exit: 0 },
  },
  {
    id: 'text.scale_settle',
    name: 'Scale Settle',
    source: 'global',
    description: 'Start slightly large and settle back to neutral scale.',
    intentTags: ['scale settle', 'settle', 'soft pop', 'overshoot'],
    version: 1,
    supportedProps: ['scale.x', 'scale.y'],
    parts: withParts({
      start: { scale: { x: 118, y: 118 } },
    }),
    offsets: { entry: 0.35, exit: 0 },
  },
  {
    id: 'text.soft_drift_in',
    name: 'Soft Drift In',
    source: 'global',
    description: 'Subtle diagonal drift into the readable hold.',
    intentTags: ['soft drift', 'gentle drift', 'subtle entry', 'cinematic in'],
    version: 1,
    supportedProps: ['position.x', 'position.y'],
    parts: withParts({
      start: { position: { x: -24, y: -18 } },
    }),
    offsets: { entry: 0.55, exit: 0 },
  },
  {
    id: 'text.hold_bounce',
    name: 'Hold Bounce',
    source: 'global',
    description: 'Gentle vertical bounce during the hold.',
    intentTags: ['hold bounce', 'gentle bounce', 'bouncy hold', 'floating bounce'],
    version: 1,
    supportedProps: ['position.y'],
    parts: neutralParts,
    offsets: { entry: 0, exit: 0 },
    microByRegion: {
      'position.y': {
        sustain: micro({ low: -8, high: 8 }, 'quarter'),
      },
    },
  },
  {
    id: 'text.hold_pulse',
    name: 'Hold Pulse',
    source: 'global',
    description: 'Subtle scale pulse during the hold.',
    intentTags: ['hold pulse', 'pulse', 'breathing text', 'scale pulse'],
    version: 1,
    supportedProps: ['scale.x', 'scale.y'],
    parts: neutralParts,
    offsets: { entry: 0, exit: 0 },
    microByRegion: {
      'scale.x': {
        sustain: micro({ low: -0.04, high: 0.05 }, 'quarter'),
      },
      'scale.y': {
        sustain: micro({ low: -0.04, high: 0.05 }, 'quarter'),
      },
    },
  },
  {
    id: 'text.fade_in',
    name: 'Fade In',
    source: 'global',
    description: 'Fade from transparent into the readable hold.',
    intentTags: ['fade in', 'opacity in', 'appear softly', 'soft reveal'],
    version: 1,
    supportedProps: ['opacity'],
    parts: neutralParts,
    offsets: { entry: 0.35, exit: 0 },
    props: {
      opacity: {
        start: { value: 0 },
        left: { value: 1 },
        right: { value: 1 },
        end: { value: 1 },
        offsets: { entry: 0.35, exit: 0 },
        entryEasing: 'Cubic.Out',
        sustainEasing: 'Linear.None',
        exitEasing: 'Linear.None',
      },
    },
  },
  {
    id: 'text.fade_out',
    name: 'Fade Out',
    source: 'global',
    description: 'Hold readable, then fade to transparent.',
    intentTags: ['fade out', 'opacity out', 'disappear softly', 'soft exit'],
    version: 1,
    supportedProps: ['opacity'],
    parts: neutralParts,
    offsets: { entry: 0, exit: 0.35 },
    props: {
      opacity: {
        start: { value: 1 },
        left: { value: 1 },
        right: { value: 1 },
        end: { value: 0 },
        offsets: { entry: 0, exit: 0.35 },
        entryEasing: 'Linear.None',
        sustainEasing: 'Linear.None',
        exitEasing: 'Cubic.In',
      },
    },
  },
]

export default GLOBAL_TEXT_MOTION_PRESETS
